import { ReactNode } from 'react';
import { Typography, Link, Stack, Container } from '@optimus-web/core';
import { PaymentTableRecord, PaymentType } from '@mews-commander/data-access';
import { useLocalizeEnum, useLocalizeText, useToggle } from '@mews/framework';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import {
    alternativePaymentMethodRefundTextKeyMap,
    alternativePaymentMethodTextKeyMap,
    paymentTypeTextKeyMap,
    refundTypeTextKeyMap,
} from '@mews-commander/utils';
import { PaymentDetailsSidebar } from '../../sidePanels/PaymentDetails/PaymentDetailsSidebar';
import { getPaymentTypeMainText, isAlternativePaymentMethodSepa } from './utils';
import { PaymentTerminalIcon } from './PaymentTerminalIcon';
import { useLocalizePaymentCardDetails } from './hooks';

interface Props {
    paymentId: string;
    linkTitle: ReactNode;
}

const PaymentSidebarLink = ({ paymentId, linkTitle }: Props) => {
    const [opened, toggleOpened] = useToggle();
    return (
        <>
            <Link inherit onClick={toggleOpened} data-test-id={DataTestId.PaymentDetailLink}>
                {linkTitle}
            </Link>

            {opened && <PaymentDetailsSidebar isOpen={opened} closeDialog={toggleOpened} paymentId={paymentId} />}
        </>
    );
};
const wrappedPaymentTypeDetails = (content: string) =>
    `${UnicodeEntity.LeftParenthesis}${content}${UnicodeEntity.RightParenthesis}`;

export const PaymentTypeCell = ({
    payment,
    isPaymentAttemptsTable = false,
}: {
    payment: PaymentTableRecord;
    isPaymentAttemptsTable?: boolean;
}) => {
    const localizeText = useLocalizeText();
    const localizeEnum = useLocalizeEnum();
    const localizePaymentCardDetails = useLocalizePaymentCardDetails();

    const getCellText = () => {
        const paymentTypeTextKey =
            payment.amount.value < 0
                ? refundTypeTextKeyMap[payment.data.discriminator]
                : paymentTypeTextKeyMap[payment.data.discriminator];

        switch (payment.data.discriminator) {
            case PaymentType.Alternative:
                return {
                    paymentTypeMainText: localizeText({
                        textKey: getPaymentTypeMainText(
                            payment,
                            alternativePaymentMethodTextKeyMap[payment.data.value.paymentMethod],
                            alternativePaymentMethodRefundTextKeyMap[payment.data.value.paymentMethod]
                        ),
                    }),
                    paymentTypeDetails: isAlternativePaymentMethodSepa(payment.data.value.paymentMethod)
                        ? wrappedPaymentTypeDetails(
                              `${localizeText({ textKey: TextKey.Sepa })} ${payment.data.value.lastFourIbanDigits}`
                          )
                        : null,
                };
            case PaymentType.CreditCard:
                return {
                    paymentTypeMainText: localizeText({
                        textKey: getPaymentTypeMainText(
                            payment,
                            paymentTypeTextKeyMap[payment.data.discriminator],
                            refundTypeTextKeyMap[payment.data.discriminator]
                        ),
                    }),
                    paymentTypeDetails: payment.data.value.lastFourCardDigits
                        ? wrappedPaymentTypeDetails(localizePaymentCardDetails(payment.data.value))
                        : localizeText({ textKey: TextKey.Unspecified }),
                };
            case PaymentType.External:
                return {
                    paymentTypeMainText: `${localizeText({
                        textKey: paymentTypeTextKey,
                    })} ${localizeEnum('ExternalPaymentType', payment.data.value.type)}`,
                };

            case PaymentType.Invoice:
                return {
                    paymentTypeMainText: `${localizeText({
                        textKey: payment.data.value.debtInvoiceId
                            ? TextKey.ReceivableInvoicePayment
                            : paymentTypeTextKey,
                    })}`,
                };
            default:
                return {
                    paymentTypeMainText: localizeText({ textKey: paymentTypeTextKey }),
                };
        }
    };

    const {
        id,
        refundedPaymentId,
        data: { value },
    } = payment;

    const paymentTypeCellText = getCellText();
    const linkContent = (
        <Stack vertical>
            <Stack spacing="50" itemAlignment="center">
                <PaymentTerminalIcon value={value} />
                <Typography textStyle="bodyMediumStrong" whiteSpace="nowrap" as="div">
                    {paymentTypeCellText.paymentTypeMainText}
                </Typography>
            </Stack>
            <Typography textStyle="bodyMediumStrong" as="div">
                {paymentTypeCellText.paymentTypeDetails}
            </Typography>
        </Stack>
    );
    return (
        <Container padding={['0', !isPaymentAttemptsTable && refundedPaymentId ? '200' : '0']}>
            <PaymentSidebarLink paymentId={id} linkTitle={linkContent} />
        </Container>
    );
};
